// Story 048 — Edit-lock state.
//
// One store per browser tab. The lock state is keyed by
// `dataAcquisitionId` so navigating between Analysis pages does not
// leak state across DAs. The "leader" flag is set by
// `useLockBroadcastChannel.ts`'s election (only one window per
// BroadcastChannel is leader; the leader is the heartbeat sender).
//
// Spec: docs/backend-requirements/095-edit-lock-heartbeat.md.
//
// The store is intentionally pure — no React hooks, no API client. The
// `useLockEffect` hook plugs the side effects in.
import { create } from "zustand";

export interface LockHolder {
  userId: string;
  displayName: string;
}

export type LockState =
  | { kind: "idle" }
  | {
      kind: "acquiring";
      dataAcquisitionId: string;
    }
  | {
      kind: "holding";
      dataAcquisitionId: string;
      lockId: string;
      holder: LockHolder;
      heldSince: string;
      isLeader: boolean;
      windowId: string;
    }
  | {
      kind: "contended";
      dataAcquisitionId: string;
      heldBy: LockHolder;
      heldSince: string;
    }
  | {
      kind: "free";
      dataAcquisitionId: string;
    };

export interface LockStoreState {
  state: LockState;
  /** Most-recent peer set seen on the BroadcastChannel (windowId set). */
  peers: ReadonlySet<string>;

  setAcquiring(dataAcquisitionId: string): void;
  setHolding(args: {
    dataAcquisitionId: string;
    lockId: string;
    holder: LockHolder;
    heldSince: string;
    windowId: string;
    isLeader: boolean;
  }): void;
  setContended(args: {
    dataAcquisitionId: string;
    heldBy: LockHolder;
    heldSince: string;
  }): void;
  setFree(dataAcquisitionId: string): void;
  setLeader(isLeader: boolean): void;
  setPeers(peers: ReadonlySet<string>): void;
  reset(): void;
}

export const useLockStore = create<LockStoreState>((set) => ({
  state: { kind: "idle" },
  peers: new Set<string>(),

  setAcquiring: (dataAcquisitionId) => {
    set({ state: { kind: "acquiring", dataAcquisitionId } });
  },

  setHolding: ({ dataAcquisitionId, lockId, holder, heldSince, windowId, isLeader }) => {
    set({
      state: {
        kind: "holding",
        dataAcquisitionId,
        lockId,
        holder,
        heldSince,
        isLeader,
        windowId,
      },
    });
  },

  setContended: ({ dataAcquisitionId, heldBy, heldSince }) => {
    set({
      state: {
        kind: "contended",
        dataAcquisitionId,
        heldBy,
        heldSince,
      },
    });
  },

  setFree: (dataAcquisitionId) => {
    set({ state: { kind: "free", dataAcquisitionId } });
  },

  setLeader: (isLeader) => {
    set((s) => {
      if (s.state.kind !== "holding") return s;
      return { state: { ...s.state, isLeader } };
    });
  },

  setPeers: (peers) => {
    set({ peers });
  },

  reset: () => {
    set({ state: { kind: "idle" }, peers: new Set<string>() });
  },
}));
